import { useEffect } from "react";
import {
  FiraSans_400Regular,
  FiraSans_500Medium,
  FiraSans_600SemiBold,
  FiraSans_700Bold,
  useFonts as useFiraSansFonts,
} from "@expo-google-fonts/fira-sans";
import {
  FiraMono_400Regular,
  FiraMono_500Medium,
  FiraMono_700Bold,
  useFonts as useFiraMonoFonts,
} from "@expo-google-fonts/fira-mono";
import { useQueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import * as Sentry from "@sentry/react-native";
import Toast from "react-native-toast-message";
import { useRef } from "react";

import { AppLifecycle, useStartupUpdateGate } from "~/lib/app-updates";
import { BackendStatusOverlay } from "~/components/BackendStatusOverlay";
import { MfaGate } from "~/components/composed/MfaGate";
import { QuerySessionBoundary } from "~/components/QuerySessionBoundary";
import { AuthProvider, useAuth } from "~/hooks/useAuth";
import { queryClient } from "~/lib/query-client";
import { persistOptions } from "~/lib/query-persist";
import { migrateGuestToRemote } from "~/state/sync";
import { ThemeProvider, useTheme } from "~/styles/theme";

void SplashScreen.preventAutoHideAsync();

function RootLayout() {
  const [firaSansLoaded, firaSansError] = useFiraSansFonts({
    FiraSans_400Regular,
    FiraSans_500Medium,
    FiraSans_600SemiBold,
    FiraSans_700Bold,
  });
  const [firaMonoLoaded, firaMonoError] = useFiraMonoFonts({
    FiraMono_400Regular,
    FiraMono_500Medium,
    FiraMono_700Bold,
  });
  const fontsReady =
    (firaSansLoaded && firaMonoLoaded) ||
    Boolean(firaSansError || firaMonoError);

  // Hold the splash until fonts load and the launch-time update check finishes (4s cap).
  const updateCheckDone = useStartupUpdateGate();
  const ready = fontsReady && updateCheckDone;

  useEffect(() => {
    if (ready) void SplashScreen.hideAsync();
  }, [ready]);

  if (!ready) return null;

  return (
    <ThemeProvider>
      <AppLifecycle />
      <BackendStatusOverlay />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => {
          void queryClient.resumePausedMutations();
        }}
      >
        <AuthProvider>
          <MfaGate>
            <QuerySessionBoundary>
              <GuestDataMigration />
              <ThemedStack />
            </QuerySessionBoundary>
          </MfaGate>
        </AuthProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}

function ThemedStack() {
  const { scheme, colors } = useTheme();
  return (
    <>
      <Stack
        screenOptions={{
          headerShown: false,
          headerStyle: { backgroundColor: colors.primary },
          contentStyle: {
            backgroundColor: colors.background,
          },
        }}
      />
      <StatusBar style={scheme === "dark" ? "light" : "dark"} />
      <Toast />
    </>
  );
}

/** When a guest signs up, push their local tasks into the new account once. */
function GuestDataMigration() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const migratedFor = useRef<string | null>(null);

  useEffect(() => {
    const userId = user?.id ?? null;
    if (!userId || migratedFor.current === userId) return;
    migratedFor.current = userId;
    void migrateGuestToRemote(queryClient, userId).catch(() => {
      migratedFor.current = null;
    });
  }, [user?.id, queryClient]);

  return null;
}

export default Sentry.wrap(RootLayout);
